import { useEffect, useState } from 'react';
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import MapRoundedIcon from '@mui/icons-material/MapRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import StreetviewRoundedIcon from '@mui/icons-material/StreetviewRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { useNavigate } from 'react-router-dom';
import { api, apiRoutes } from '../../api';
import Header from '../../shared/layout/Header';
import AgentAssignments from './AgentAssignments';
import AgentCalendar from './AgentCalendar';
import IssueReporting from './categories/IssueReporting';
import NeighborhoodObservations from './categories/NeighborhoodObservations';
import POIUpdates from './categories/POIUpdates';
import ParcelAssessments from './categories/ParcelAssessments';
import StreetObservations from './categories/StreetObservations';
import Surveys from './categories/Surveys';
import './AgentsPanel.css';

const tabs = [
  { label: 'وظایف', icon: AssignmentRoundedIcon, Panel: AgentAssignments, badgeKey: 'assignments' },
  { label: 'تقویم', icon: CalendarMonthRoundedIcon, Panel: AgentCalendar },
  { label: 'محله', icon: LocationOnRoundedIcon, Panel: NeighborhoodObservations },
  { label: 'خیابان‌ها', icon: StreetviewRoundedIcon, Panel: StreetObservations },
  { label: 'نقاط فعالیتی', icon: StorefrontRoundedIcon, Panel: POIUpdates },
  { label: 'قطعات ساختمانی', icon: BusinessRoundedIcon, Panel: ParcelAssessments },
  { label: 'مشکلات', icon: WarningAmberRoundedIcon, Panel: IssueReporting, badgeKey: 'issues' },
  { label: 'مصاحبه‌ها', icon: PersonRoundedIcon, Panel: Surveys, badgeKey: 'surveys' },
];

const initialStats = { assignments: 0, surveys: 0, issues: 0, showings: 0 };

export default function AgentsPanel() {
  const [activeTab, setActiveTab] = useState(0);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(initialStats);
  const navigate = useNavigate();

  useEffect(() => {
    const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

    const checkAgentStatus = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) {
        navigate('/login');
        setLoading(false);
        return;
      }

      try {
        const { data } = await api.get(apiRoutes.auth.user, { headers: authHeader() });
        const isAgent = data.groups?.some((group) => group.toLowerCase() === 'agents')
          || data.is_agent
          || data.agent_profile
          || data.user_type === 'agent';

        if (!isAgent) {
          setError('دسترسی به این بخش فقط برای نمایندگان فعال است.');
          return;
        }
        setUser(data);
      } catch (requestError) {
        if (requestError.response?.status === 401) {
          localStorage.removeItem('access_token');
          navigate('/login');
        } else {
          setError('تأیید حساب نماینده انجام نشد. لطفاً دوباره تلاش کنید.');
        }
      } finally {
        setLoading(false);
      }
    };

    const loadStats = async () => {
      if (!localStorage.getItem('access_token')) return;
      const fallback = { data: { count: 0, results: [] } };
      try {
        const [assignmentsRes, surveysRes, issuesRes, showingsRes] = await Promise.all([
          api.get(apiRoutes.agents.propertyAssignments, { headers: authHeader() }).catch(() => fallback),
          api.get(apiRoutes.developers.surveyCampaigns, { headers: authHeader() }).catch(() => fallback),
          api.get(apiRoutes.agentIssues.list, { headers: authHeader() }).catch(() => fallback),
          api.get(apiRoutes.users.showings, { headers: authHeader() }).catch(() => fallback),
        ]);
        const showings = showingsRes.data.results || showingsRes.data || [];
        setStats({
          assignments: assignmentsRes.data.count || assignmentsRes.data.results?.length || 0,
          surveys: surveysRes.data.count || surveysRes.data.results?.length || 0,
          issues: issuesRes.data.count || issuesRes.data.results?.length || 0,
          showings: Array.isArray(showings) ? showings.filter(({ status }) => status === 'REQUESTED').length : 0,
        });
      } catch {
        setStats(initialStats);
      }
    };

    checkAgentStatus();
    loadStats();
  }, [navigate]);

  if (loading) {
    return (
      <div className="agents-page" dir="rtl">
        <Header />
        <main className="agents-state" role="status">
          <span className="agents-spinner" />
          <p>در حال آماده‌سازی پنل نمایندگان…</p>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="agents-page" dir="rtl">
        <Header />
        <main className="agents-shell"><div className="agents-alert error" role="alert">{error}</div></main>
      </div>
    );
  }

  const displayName = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.username || 'نماینده';
  const ActivePanel = tabs[activeTab].Panel;
  const kpis = [
    { value: stats.assignments, label: 'وظایف فعال', hint: 'ماموریت‌های تخصیص‌یافته', icon: AssignmentRoundedIcon, tone: 'teal' },
    { value: stats.showings, label: 'بازدید در انتظار', hint: 'نیازمند زمان‌بندی', icon: CalendarMonthRoundedIcon, tone: 'blue' },
    { value: stats.surveys, label: 'مصاحبه ثبت‌شده', hint: 'داده‌های میدانی', icon: MapRoundedIcon, tone: 'green' },
    { value: stats.issues, label: 'مشکلات گزارش‌شده', hint: 'گزارش‌های ثبت‌شده', icon: WarningAmberRoundedIcon, tone: 'amber' },
  ];

  return (
    <div className="agents-page agents-panel-root" dir="rtl">
      <Header />
      <main className="agents-shell">
        <section className="agents-hero">
          <div>
            <p className="agents-kicker">مرکز عملیات میدانی</p>
            <h1>پنل نمایندگان</h1>
            <p>وظایف، بازدیدها و داده‌های مکانی را از یک فضای یکپارچه مدیریت کنید.</p>
          </div>
          <div className="agents-user-card" aria-label={`کاربر واردشده: ${displayName}`}>
            <span>{displayName.charAt(0)}</span>
            <div><small>نماینده فعال</small><strong>{displayName}</strong></div>
          </div>
        </section>

        <section className="agents-kpis" aria-label="خلاصه عملکرد">
          {kpis.map(({ value, label, hint, icon: Icon, tone }) => (
            <article className="agents-kpi" key={label}>
              <span className={`agents-kpi-icon ${tone}`}><Icon /></span>
              <div><small>{label}</small><strong>{value.toLocaleString('fa-IR')}</strong><p>{hint}</p></div>
            </article>
          ))}
        </section>

        <nav className="agents-tabs" aria-label="بخش‌های پنل نمایندگان">
          {tabs.map(({ label, icon: Icon, badgeKey }, index) => (
            <button
              type="button"
              key={label}
              className={activeTab === index ? 'active' : ''}
              aria-current={activeTab === index ? 'page' : undefined}
              onClick={() => setActiveTab(index)}
            >
              <Icon /><span>{label}</span>
              {badgeKey && stats[badgeKey] > 0 && <b>{stats[badgeKey].toLocaleString('fa-IR')}</b>}
            </button>
          ))}
        </nav>

        <section className="agents-workspace" aria-live="polite">
          <ActivePanel />
        </section>
      </main>
    </div>
  );
}
